import { Check, Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  formatQuizDateTime,
  formatRemainingTime,
  nextAttemptIn,
  percentOf,
} from "@/lib/courseCompletionQuizFormat";
import type {
  CompletionQuizResult,
  CompletionQuizReviewedQuestion,
  CourseCompletionQuizSummary,
} from "@/types/courseCompletionQuiz";

interface CourseCompletionQuizResultProps {
  result: CompletionQuizResult;
  summary: CourseCompletionQuizSummary;
  courseTitle: string;
  firstName?: string;
  onDashboard: () => void;
  onCourse: () => void;
  /** Starts the next attempt; only offered once the lock is over. */
  onNextAttempt: () => void;
  now?: Date;
}

const RING_RADIUS = 52;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/** Green when the quiz is passed, error when no attempt is left, otherwise the progress color. */
const scoreStroke = (result: CompletionQuizResult) => {
  if (result.state === "passed") return "stroke-success";
  if (result.status === "failed_final") return "stroke-error";
  return "stroke-secondary";
};

const ScoreRing = ({
  percent,
  limit,
  strokeClass,
}: {
  percent: number;
  limit: number;
  strokeClass: string;
}) => {
  const angle = (limit / 100) * 2 * Math.PI - Math.PI / 2;
  return (
    <div className="relative size-36 shrink-0">
      <svg viewBox="0 0 120 120" className="size-full -rotate-90" aria-hidden>
        <circle
          cx="60"
          cy="60"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="9"
          className="stroke-border"
        />
        <circle
          cx="60"
          cy="60"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={RING_LENGTH}
          strokeDashoffset={RING_LENGTH * (1 - percent / 100)}
          className={strokeClass}
        />
      </svg>
      <span
        aria-hidden
        className="absolute size-1.5 rounded-full bg-primary"
        style={{
          left: `${60 + RING_RADIUS * Math.cos(angle)}px`,
          top: `${60 + RING_RADIUS * Math.sin(angle)}px`,
          transform: "translate(-50%, -50%)",
        }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-h1 font-bold text-primary">{percent} %</span>
        <span className="text-xs text-muted-foreground">Grenze: {limit} %</span>
      </div>
    </div>
  );
};

const headline = (
  result: CompletionQuizResult,
  passed: boolean,
  firstName?: string
): string => {
  if (passed && firstName) {
    return `Glückwunsch, ${firstName} – du hast den Kurs bestanden!`;
  }
  if (passed) return "Glückwunsch – du hast den Kurs bestanden!";
  if (result.requiredCorrect - result.correctCount === 1) {
    return "Knapp daneben – dir fehlt nur eine richtige Antwort";
  }
  return "Leider nicht bestanden";
};

const passedSummary = (
  result: CompletionQuizResult,
  summary: CourseCompletionQuizSummary,
  courseTitle: string
) =>
  `${result.correctCount} von ${result.totalCount} richtig. Die Bestehensgrenze liegt bei ${summary.passPercent} % (${result.requiredCorrect} Fragen). „${courseTitle}“ ist jetzt als abgeschlossen markiert.`;

const failedSummary = (
  result: CompletionQuizResult,
  summary: CourseCompletionQuizSummary,
  percent: number
) =>
  `${result.correctCount} von ${result.totalCount} richtig (${percent} %). Zum Bestehen brauchst du ${summary.passPercent} %, also mindestens ${result.requiredCorrect} richtige Antworten.`;

const ScoreSummary = ({
  result,
  summary,
  courseTitle,
  percent,
  passed,
}: {
  result: CompletionQuizResult;
  summary: CourseCompletionQuizSummary;
  courseTitle: string;
  percent: number;
  passed: boolean;
}) => {
  if (passed) {
    return (
      <p className="mt-3 text-foreground">
        {passedSummary(result, summary, courseTitle)}
      </p>
    );
  }
  return (
    <p className="mt-3 text-foreground">
      {failedSummary(result, summary, percent)}
    </p>
  );
};

const remainingWait = (result: CompletionQuizResult, now: Date) => {
  if (!result.nextAttemptAt) return null;
  return formatRemainingTime(result.nextAttemptAt, now);
};

const attemptIsReady = (
  result: CompletionQuizResult,
  remaining: string | null
) =>
  result.status === "startable" || (result.status === "waiting" && !remaining);

const attemptWord = (count: number) => (count === 1 ? "Versuch" : "Versuche");

const waitDetail = (remaining: string | null, attemptsLeft: number) => {
  const left = `noch ${attemptsLeft} ${attemptWord(attemptsLeft)} übrig`;
  if (!remaining) return left;
  return `Noch ${remaining} Wartezeit · ${left}`;
};

const nextStepTitle = (result: CompletionQuizResult, ready: boolean) => {
  if (result.nextAttemptAt && !ready) {
    return `Nächster Versuch ab ${formatQuizDateTime(result.nextAttemptAt)} Uhr`;
  }
  return "Du kannst einen neuen Versuch starten";
};

const NextAttemptAction = ({
  ready,
  remaining,
  onNextAttempt,
}: {
  ready: boolean;
  remaining: string | null;
  onNextAttempt: () => void;
}) => {
  if (ready) {
    return (
      <Button type="button" size="lg" onClick={onNextAttempt}>
        Neuer Versuch
      </Button>
    );
  }
  return (
    <span className="rounded-lg bg-card px-3 py-2 text-sm font-semibold text-muted-foreground">
      {remaining ? nextAttemptIn(remaining) : "Neuer Versuch in"}
    </span>
  );
};

const AnswerMark = ({ correct }: { correct: boolean | null }) => {
  if (correct) {
    return <Check className="size-3.5" aria-label="richtig" />;
  }
  return <X className="size-3.5" aria-label="falsch" />;
};

const answerTone = (correct: boolean | null) => {
  if (correct) return "bg-success-light text-success-foreground";
  return "bg-error-light text-error-foreground";
};

const NextStep = ({
  result,
  summary,
  onNextAttempt,
  now,
}: Pick<
  CourseCompletionQuizResultProps,
  "result" | "summary" | "onNextAttempt"
> & {
  now: Date;
}) => {
  if (result.status === "failed_final") {
    return (
      <div className="mt-4 rounded-xl bg-info-light p-5">
        <p className="font-bold text-primary">
          Du hast alle {summary.maxAttempts} Versuche genutzt
        </p>
        <p className="mt-1 text-sm text-foreground">
          Ein weiterer Versuch ist nicht möglich. Wende dich an deine
          Kursbegleitung, um zu klären, wie du den Kurs trotzdem abschließen
          kannst.
        </p>
      </div>
    );
  }

  const remaining = remainingWait(result, now);
  const ready = attemptIsReady(result, remaining);

  return (
    <div className="mt-4 flex flex-wrap items-center gap-4 rounded-xl bg-info-light p-5">
      <span className="flex size-10 items-center justify-center rounded-lg bg-card text-primary">
        <Clock className="size-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-bold text-primary">{nextStepTitle(result, ready)}</p>
        <p className="text-sm text-muted-foreground">
          {waitDetail(remaining, result.attemptsLeft)}
        </p>
      </div>
      <NextAttemptAction
        ready={ready}
        remaining={remaining}
        onNextAttempt={onNextAttempt}
      />
    </div>
  );
};

const AnswerReview = ({
  questions,
}: {
  questions: CompletionQuizReviewedQuestion[];
}) => (
  <Card className="gap-0 px-6 py-6">
    <h2>Deine Antworten</h2>
    <ol className="mt-4 flex flex-wrap gap-2">
      {questions.map((question, index) => (
        <li
          key={`${question.contentElementId}:${question.entryIndex}`}
          className={cn(
            "flex min-w-14 items-center justify-center gap-1 rounded-lg px-3 py-2 text-sm font-bold",
            answerTone(question.correct)
          )}
        >
          {index + 1}
          <AnswerMark correct={question.correct} />
        </li>
      ))}
    </ol>
  </Card>
);

const renderAnswerReview = (
  over: boolean,
  questions: CompletionQuizReviewedQuestion[] | undefined
) => {
  if (!over || !questions) return null;
  return <AnswerReview questions={questions} />;
};

const renderHeldBackNote = (over: boolean, passed: boolean) => {
  if (over || passed) return null;
  return (
    <p className="px-5 text-sm text-muted-foreground">
      Welche Antworten richtig waren, siehst du, sobald du das Quiz bestanden
      oder alle Versuche genutzt hast.
    </p>
  );
};

const renderNextStep = (
  passed: boolean,
  props: Pick<
    CourseCompletionQuizResultProps,
    "result" | "summary" | "onNextAttempt"
  > & { now: Date }
) => {
  if (passed) return null;
  return <NextStep {...props} />;
};

const dashboardVariant = (ready: boolean) => {
  if (ready) return "secondary" as const;
  return "default" as const;
};

const ResultActions = ({
  passed,
  ready,
  onCourse,
  onDashboard,
}: {
  passed: boolean;
  ready: boolean;
  onCourse: () => void;
  onDashboard: () => void;
}) => {
  if (passed) {
    return (
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          onClick={onDashboard}
        >
          Zum Dashboard
        </Button>
        <Button type="button" size="lg" onClick={onCourse}>
          Zum Kurs
        </Button>
      </div>
    );
  }
  return (
    <div className="flex justify-end gap-3">
      <Button
        type="button"
        variant={dashboardVariant(ready)}
        size="lg"
        onClick={onDashboard}
      >
        Zum Dashboard
      </Button>
    </div>
  );
};

/**
 * Evaluation of the last finished run. While the quiz can still be retried only
 * the score is shown; which questions were right comes once the quiz is over.
 */
export const CourseCompletionQuizResult = ({
  result,
  summary,
  courseTitle,
  firstName,
  onDashboard,
  onCourse,
  onNextAttempt,
  now = new Date(),
}: CourseCompletionQuizResultProps) => {
  const passed = result.state === "passed";
  const percent = percentOf(result.correctCount, result.totalCount);
  const over = result.status === "passed" || result.status === "failed_final";
  const ready = attemptIsReady(result, remainingWait(result, now));

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-6 md:px-8">
      <div className="flex flex-wrap items-center gap-8 py-4">
        <ScoreRing
          percent={percent}
          limit={summary.passPercent}
          strokeClass={scoreStroke(result)}
        />
        <div className="min-w-0 flex-1">
          <h1
            style={
              { "--heading-color": "var(--primary)" } as React.CSSProperties
            }
          >
            {headline(result, passed, firstName)}
          </h1>
          <ScoreSummary
            result={result}
            summary={summary}
            courseTitle={courseTitle}
            percent={percent}
            passed={passed}
          />
        </div>
      </div>

      {renderNextStep(passed, { result, summary, onNextAttempt, now })}
      {renderAnswerReview(over, result.questions)}
      {renderHeldBackNote(over, passed)}

      <ResultActions
        passed={passed}
        ready={ready}
        onCourse={onCourse}
        onDashboard={onDashboard}
      />
    </div>
  );
};
